import React, { FC } from 'react';
import {
	VictoryChart,
	VictoryAxis,
	VictoryTooltip,
	VictoryBar,
	VictoryLabel,
} from 'victory';

import colors from '../../../../assets/colors';
import { fontFamily } from '../../../../assets/themes';
import { formatToCurrency, kFormatter } from '../../../../core/helpers/numberFormatter';
import useWindowSize from '../../../../core/hooks/useWindowSize';

type IProps = {
	data: any;
	chartWidth: number;
	grouping?: {
		groupingLevel1?: string;
		groupingLevel2?: string;
	};
	name?: string;
};

const barColor = '#2253FF';
const maxlimit = 13;
const DEFAULT_SNAPS_NAMES = [
	'top20CustomersByTotalBookingAmount',
	'currentQuarterPipelineByStageAndOwner',
	'Top 20 customers by total booking amount',
	'Current quarter pipeline by stage and owner',
];

const Chart: FC<IProps> = ({ data, chartWidth, grouping, name }) => {
	const { height, width } = useWindowSize();
	const chartHeight = height / 2.2;
	const numberWithCommas = (x: string) =>
		x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

	const formattedData = ((data?.length && grouping?.groupingLevel1 === 'Amount')
		? data?.map((item: any) => !isNaN(+item._id)
			? ({ ...item, _id: formatToCurrency(+item._id) })
			: item
		)
		: data)
		.sort((a: any, b:any) => a.count < b.count? 1 : -1);

	return (
		<VictoryChart
			padding={{
				top: 30,
				bottom: 56,
				left: 0,
				right: 0,
			}}
			{...(width > 1024 ? { height: chartHeight } : null)}
			width={chartWidth}
			domainPadding={{ x: 120, y: 20 }}
		>
			<VictoryAxis
				dependentAxis
				offsetX={-5}
				tickFormat={t =>
					DEFAULT_SNAPS_NAMES.includes(String(name))
						? formatToCurrency(+t)
						: kFormatter(t.toString(), true)
				}
				tickLabelComponent={
					<VictoryLabel dy={-14} dx={30} textAnchor="start" />
				}
				style={{
					axis: { stroke: 'transparent', strokeWidth: 2 },
					tickLabels: {
						fontFamily: fontFamily.primaryRegular,
						fontSize: 12,
					},
					grid: {
						stroke: colors.border,
						strokeWidth: 1,
						strokeDasharray: 0,
					},
				}}
			/>
			<VictoryBar
				x="_id"
				y="count"
				data={formattedData}
				// cornerRadius={{
				// 	topLeft: 8,
				// 	topRight: 8,
				// }}
				barWidth={50}
				labels={({ datum }) =>
					DEFAULT_SNAPS_NAMES.includes(String(name))
						? formatToCurrency(+datum.count)
						: numberWithCommas(datum.count)
				}
				style={{
					data: {
						fill: barColor,
						strokeWidth: 0,
						stroke: '#000000',
					},
				}}
				labelComponent={
					<VictoryTooltip
						active={true}
						dy={-10}
						style={{
							width: 38,
							fontSize: 12,
							fill: '#000',
							fontWeight: 600,
							fontFamily: fontFamily.primaryRegular,
						}}
						cornerRadius={5}
						orientation="top"
						flyoutWidth={40}
						flyoutHeight={20}
						pointerLength={0}
						flyoutStyle={{ fill: '#fff', strokeWidth: 0 }}
						renderInPortal={false}
					/>
				}
				events={[
					{
						target: 'data',
						eventHandlers: {
							onClick: (evt, pressedProps) => {
								const selectedDataIndex = pressedProps.index;
								return {
									eventKey: 'all',
									target: 'data',
									mutation: props => {
										return props.index === selectedDataIndex
											? {
												active: true,
												style: {
													fill: barColor,
													stroke: barColor,
													strokeWidth: 0,
												},
											}
											: {
												style: {
													fill: barColor,
													stroke: barColor,
													strokeWidth: 0,
												},
											};
									},
								};
							},
						},
					},
				]}
			/>
			<VictoryAxis
				tickFormat={t =>
					t
						.toString()
						.split(' ')
						.map((el: string) =>
							el.length > maxlimit ? el.substring(0, maxlimit - 1) + '...' : el
						)
				}
				style={{
					axis: { stroke: colors.grey },
					tickLabels: {
						fontFamily: fontFamily.primaryRegular,
						fontWeight: 700,
						fontSize: 14,
						padding: 10,
					},
				}}
			/>
		</VictoryChart>
	);
};

export default React.memo(Chart);
